"use client";

import React, { useState, ChangeEvent, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, X, Image as ImageIcon, GalleryHorizontal, CheckCircle } from '@/components/icons';
import { useToast } from '@/components/ui/use-toast';
import { uploadProductImage } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';
import ImageManagerDialog from './ImageManagerDialog';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

// Dnd-kit imports
import { arrayMove, DragEndEvent } from '@dnd-kit/sortable';
import ImageStage from './ImageStage'; // Importar el nuevo componente ImageStage

interface ProductImageManagerProps {
  session: Session | null;
  initialImageUrls: string[];
  initialMainImageUrl: string | null;
  onImagesChange: (
    allImageUrls: string[],
    mainImageUrl: string | null,
    imagesToDelete: string[]
  ) => void;
  disabled?: boolean;
}

const MAX_IMAGES = 15;

const ProductImageManager: React.FC<ProductImageManagerProps> = ({
  session,
  initialImageUrls,
  initialMainImageUrl,
  onImagesChange,
  disabled,
}) => {
  const [currentImages, setCurrentImages] = useState<string[]>(initialImageUrls);
  const [mainImage, setMainImage] = useState<string | null>(initialMainImageUrl);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);

  const [isUploading, setIsUploading] = useState(false);
  const [isImageManagerOpen, setIsImageManagerOpen] = useState(false);
  const { toast } = useToast();

  // Sync with props if they change (e.g. after a catalog fetch or initial load)
  useEffect(() => {
    if (initialImageUrls.length > 0) {
      let orderedImages = [...initialImageUrls];
      if (initialMainImageUrl && initialImageUrls.includes(initialMainImageUrl)) {
        orderedImages = [
          initialMainImageUrl,
          ...initialImageUrls.filter(url => url !== initialMainImageUrl)
        ];
      }
      setCurrentImages(orderedImages);
      setMainImage(orderedImages.length > 0 ? orderedImages[0] : null);
    }
  }, [initialImageUrls, initialMainImageUrl]);

  // Notify parent of changes whenever internal image states change
  useEffect(() => {
    // La imagen principal es siempre la primera de currentImages
    const newMainImage = currentImages.length > 0 ? currentImages[0] : null;
    setMainImage(newMainImage);
    onImagesChange(currentImages, newMainImage, imagesToDelete);
  }, [currentImages, imagesToDelete, onImagesChange]);

  const handleSelectFromGallery = (urls: string | string[]) => {
    // Synchronous update: set final images in one go to avoid an intermediate empty state
    const selectedUrls = Array.isArray(urls) ? urls : [urls];

    // Determinar las imágenes que se eliminaron (para borrarlas del storage si es necesario)
    const removedImages = currentImages.filter((url) => !selectedUrls.includes(url));

    setImagesToDelete((prevDelete) => {
      const newDeletes = [...prevDelete];
      removedImages.forEach((url) => {
        if (initialImageUrls.includes(url) && !newDeletes.includes(url)) {
          newDeletes.push(url);
        }
      });
      return newDeletes;
    });

    // Actualizar el stager inmediatamente con las imágenes seleccionadas
    setCurrentImages(Array.from(new Set(selectedUrls)));
    setIsImageManagerOpen(false);
  };

  const handleRemoveImage = (urlToRemove: string) => {
    setCurrentImages((prev) => {
      const newImages = prev.filter((url) => url !== urlToRemove);
      
      // Solo añadir a imagesToDelete si era una imagen inicial (ya en DB)
      if (initialImageUrls.includes(urlToRemove)) {
        setImagesToDelete((prevDelete) => [...prevDelete, urlToRemove]);
      }
      return newImages;
    });
    toast({ title: "Imagen eliminada", description: "La imagen ha sido eliminada de la lista.", });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setCurrentImages((items) => {
        const oldIndex = items.findIndex(item => item === active.id);
        const newIndex = items.findIndex(item => item === over?.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Manejar drop de archivos desde ImageStage
  useEffect(() => {
    const handleImageDrop = (e: any) => {
      const files: File[] = e.detail?.files || [];
      if (files.length > 0 && !disabled) {
          setIsUploading(true);
          const uploadPromises = files.map(async (file) => {
            try {
              if (!session?.user?.id) throw new Error('No user session');
              const url = await uploadProductImage(file, session.user.id);
              return url;
            } catch (e) {
              toast({ title: 'Error al subir imagen', description: 'No se pudo subir la imagen.' });
              return null;
            }
          });
          Promise.all(uploadPromises).then((urls) => {
            const validUrls = urls.filter(Boolean) as string[];
            if (validUrls.length > 0) {
              setCurrentImages((prev) => {
                const next = [...prev, ...validUrls];
                // Limitar a MAX_IMAGES
                // Abrir galería solo si no está abierta
                if (!isImageManagerOpen) {
                  setTimeout(() => setIsImageManagerOpen(true), 0);
                }
                return next.slice(0, MAX_IMAGES);
              });
            }
          }).finally(() => {
            setIsUploading(false);
          });
      }
    };
    window.addEventListener('image-drop', handleImageDrop);
    return () => window.removeEventListener('image-drop', handleImageDrop);
  }, [session, disabled]);

  const handleOpenGallery = () => {
    setIsImageManagerOpen(true);
  };

  return (
    <div className="grid gap-2">
      <Label>Imágenes (hasta {MAX_IMAGES})</Label>
      <div className="flex flex-wrap gap-2 mb-4">
        <ImageStage
          currentImages={currentImages}
          onDragEnd={handleDragEnd}
          onRemoveImage={handleRemoveImage}
          disabled={disabled}
          isUploading={isUploading}
          onOpenGallery={handleOpenGallery}
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="outline"
          onClick={handleOpenGallery}
          disabled={disabled || isUploading}
          className="w-full"
        >
          <GalleryHorizontal className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
          Abrir Galería (Subir o Seleccionar)
        </Button>
      </div>

      {session && (
        <ImageManagerDialog
          session={session}
          onSelect={handleSelectFromGallery}
          isOpen={isImageManagerOpen}
          onClose={() => setIsImageManagerOpen(false)}
          allowUpload={true}
          allowMultipleSelect={true} // Habilitar selección múltiple
          maxSelection={MAX_IMAGES}
          initialSelectedUrls={currentImages} // Pasar las imágenes actuales como seleccionadas inicialmente
        />
      )}
    </div>
  );
};

export default ProductImageManager;